import api from './api'

const CONSENT_COOKIE_NAME = 'cookie_consent'
const CONSENT_EXPIRY_DAYS = 365
const PENDING_CONSENT_LOG = 'pending_consent_log'

const getBasePath = () => {
  return '/cookie-consent'
}

export const cookieCategories = {
  necessary: {
    name: 'Necessary',
    description: 'Essential cookies for basic site functionality',
    required: true
  },
  analytics: {
    name: 'Analytics',
    description: 'Help us understand how visitors use our website',
    required: false
  },
  marketing: {
    name: 'Marketing',
    description: 'Used to track visitors across websites for advertising',
    required: false
  },
  preferences: {
    name: 'Preferences',
    description: 'Remember your preferences and settings',
    required: false
  }
}

class CookieService {
  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop().split(';').shift()
    }
    return null
  }

  setCookie(name, value, days = 365) {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    const expires = `expires=${date.toUTCString()}`
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`
  }

  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`
  }

  getConsentStatus() {
    const consent = localStorage.getItem(CONSENT_COOKIE_NAME)
    if (consent) {
      try {
        return JSON.parse(consent)
      } catch (e) {
        return null
      }
    }
    return null
  }

  saveConsentStatus(categories) {
    console.log('saveConsentStatus called with categories:', categories)
    const consentData = {
      timestamp: new Date().toISOString(),
      categories: categories
    }
    localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(consentData))
    this.setCookie(CONSENT_COOKIE_NAME, 'granted', CONSENT_EXPIRY_DAYS)
    
    // Check if user is logged in
    const user = window.localStorage.getItem('user')
    const email = window.localStorage.getItem('email')
    console.log('User from localStorage:', user, 'Email:', email)
    
    // User is logged in if user exists and is not 'null' or null
    if (user && user !== 'null' && user !== null) {
      // User is logged in, send immediately
      console.log('User is logged in, calling logConsentToServer')
      this.logConsentToServer(consentData)
    } else {
      // User not logged in, store for later
      console.log('User not logged in, storing for later')
      this.storePendingConsentLog(consentData)
    }
  }

  storePendingConsentLog(consentData) {
    const logData = {
      ...consentData,
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.generateSessionId()
    }
    localStorage.setItem(PENDING_CONSENT_LOG, JSON.stringify(logData))
    console.log('Consent saved for post-login logging')
  }

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
  }

  async logConsentToServer(consentData) {
    console.log('logConsentToServer called with:', consentData)
    try {
      const user = window.localStorage.getItem('user')
      const email = window.localStorage.getItem('email')
      
      if (!user || user === 'null' || user === null) {
        console.log('User not logged in, deferring consent log')
        return
      }

      const logData = {
        ...consentData,
        userAgent: consentData.userAgent || navigator.userAgent,
        url: consentData.url || window.location.href,
        userEmail: email,
        sessionId: consentData.sessionId || this.generateSessionId(),
      }
      
      console.log('Sending consent log to API:', logData)
      console.log('API endpoint:', `${getBasePath()}/logs`)
      
      const response = await api.execute('post', `${getBasePath()}/logs`, logData)
      
      console.log('API response:', response)
      
      if (response && response.success) {
        console.log('Consent logged successfully')
        // Clear pending log if exists
        localStorage.removeItem(PENDING_CONSENT_LOG)
      } else {
        console.error('Failed to log consent:', response)
      }
    } catch (error) {
      console.error('Error logging consent:', error)
    }
  }

  async sendPendingConsentLogs() {
    const pendingLog = localStorage.getItem(PENDING_CONSENT_LOG)
    if (pendingLog) {
      try {
        const logData = JSON.parse(pendingLog)
        await this.logConsentToServer(logData)
      } catch (error) {
        console.error('Error sending pending consent log:', error)
      }
    }
  }

  clearConsent() {
    localStorage.removeItem(CONSENT_COOKIE_NAME)
    this.deleteCookie(CONSENT_COOKIE_NAME)
  }

  hasConsent() {
    return this.getConsentStatus() !== null
  }

  getCategoryConsent(category) {
    const consent = this.getConsentStatus()
    if (!consent) return false
    return consent.categories[category] === true
  }

  initializeAnalytics() {
    if (this.getCategoryConsent('analytics')) {
      console.log('Analytics cookies enabled')
      // Update Google consent if gtag is loaded
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        })
      }
    } else {
      // Revoke analytics consent
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        })
      }
      this.clearAnalyticsCookies()
    }
  }

  initializeMarketing() {
    if (this.getCategoryConsent('marketing')) {
      console.log('Marketing cookies enabled')
      // Update consent for advertising
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted'
        })
      }
    } else {
      // Revoke marketing consent
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied'
        })
      }
      this.clearMarketingCookies()
    }
  }

  clearAnalyticsCookies() {
    // Clear Google Analytics cookies
    const gaCookies = ['_ga', '_gid', '_gat', '_gac_']
    gaCookies.forEach(cookiePrefix => {
      document.cookie.split(';').forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim()
        if (cookieName.startsWith(cookiePrefix)) {
          this.deleteCookie(cookieName)
        }
      })
    })
  }

  clearMarketingCookies() {
    // Clear marketing/advertising cookies
    const marketingCookies = ['_fbp', 'fr', 'tr', '_gcl_', '_dc_gtm_']
    marketingCookies.forEach(cookiePrefix => {
      document.cookie.split(';').forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim()
        if (cookieName.startsWith(cookiePrefix)) {
          this.deleteCookie(cookieName)
        }
      })
    })
  }

  blockNonEssentialCookies() {
    // This function can be expanded to block specific third-party scripts
    // until consent is given
    const consent = this.getConsentStatus()
    if (!consent) {
      // Block analytics
      if (window.gtag) {
        window.gtag('consent', 'default', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied'
        })
      }
    }
  }
}

export default new CookieService()